import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import api from '../api';
import { 
  FileText, Plus, Eye, Edit, Trash2, CheckCircle, Clock, 
  AlertCircle, Filter, Search, FileCheck, ClipboardList 
} from 'lucide-react';

const ClinicalFormsPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('oasis');
  const [loading, setLoading] = useState(true);
  const [forms, setForms] = useState({
    admissionConsents: [],
    oasisAssessments: [],
    dischargeAssessments: []
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    fetchAllForms();
  }, []);

  const fetchAllForms = async () => {
    setLoading(true);
    try {
      const [admissionRes, oasisRes, dischargeRes] = await Promise.all([
        api.get('/forms/admission-consent'),
        api.get('/forms/oasis'),
        api.get('/forms/discharge')
      ]);

      setForms({
        admissionConsents: admissionRes.data.forms || [],
        oasisAssessments: oasisRes.data.assessments || [],
        dischargeAssessments: dischargeRes.data.assessments || []
      });
    } catch (error) {
      console.error('Error fetching forms:', error);
      toast.error('Failed to load forms');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Draft': { color: 'bg-gray-100 text-gray-700', icon: Clock },
      'In Progress': { color: 'bg-blue-100 text-blue-700', icon: Edit },
      'Completed': { color: 'bg-green-100 text-green-700', icon: CheckCircle },
      'Signed': { color: 'bg-purple-100 text-purple-700', icon: FileCheck },
      'Submitted': { color: 'bg-teal-100 text-teal-700', icon: FileCheck }
    };
    
    const config = statusConfig[status] || statusConfig['Draft'];
    const Icon = config.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        <Icon className="w-3 h-3" />
        {status}
      </span>
    );
  };

  const filterForms = (formList) => {
    return formList.filter(form => {
      const matchesSearch = form.patientName?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesStatus = statusFilter === 'all' || form.status === statusFilter;
      return matchesSearch && matchesStatus;
    });
  };

  const tabs = [
    { 
      id: 'oasis', 
      label: 'OASIS Assessments', 
      icon: ClipboardList,
      count: forms.oasisAssessments.length,
      description: 'SOC, ROC, and Recertification assessments'
    },
    { 
      id: 'admission', 
      label: 'Admission Consent', 
      icon: FileText,
      count: forms.admissionConsents.length,
      description: 'Patient admission consent forms'
    },
    { 
      id: 'discharge', 
      label: 'Discharge Assessments', 
      icon: FileCheck,
      count: forms.dischargeAssessments.length,
      description: 'Patient discharge documentation'
    }
  ];

  const renderFormList = () => {
    let currentForms = [];
    let createPath = '';
    let editPath = '';

    switch (activeTab) {
      case 'oasis':
        currentForms = filterForms(forms.oasisAssessments);
        createPath = '/dashboard/forms/oasis';
        editPath = '/dashboard/forms/oasis';
        break;
      case 'admission':
        currentForms = filterForms(forms.admissionConsents);
        createPath = '/dashboard/forms/admission-consent';
        editPath = '/dashboard/forms/admission-consent';
        break;
      case 'discharge':
        currentForms = filterForms(forms.dischargeAssessments);
        createPath = '/dashboard/forms/discharge';
        editPath = '/dashboard/forms/discharge';
        break;
      default:
        break;
    }

    if (loading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#f39c12]"></div>
        </div>
      );
    }

    if (currentForms.length === 0) {
      return (
        <div className="text-center py-12">
          <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">No forms found</h3>
          <p className="text-gray-500 mb-4">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your filters'
              : 'Create your first form to get started'}
          </p>
          <Button 
            onClick={() => navigate(createPath)}
            className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> Create New Form
          </Button>
        </div>
      );
    }

    return (
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Patient</th>
              {activeTab === 'oasis' && (
                <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Type</th>
              )}
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Date</th>
              <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Status</th>
              <th className="text-right px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {currentForms.map(form => (
              <tr key={form.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{form.patientName}</div>
                </td>
                {activeTab === 'oasis' && (
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                      {form.assessmentType}
                    </span>
                  </td>
                )}
                <td className="px-4 py-3 text-sm text-gray-600">
                  {activeTab === 'discharge' ? form.dischargeDate : form.assessmentDate || form.createdAt?.split('T')[0]}
                </td>
                <td className="px-4 py-3">
                  {getStatusBadge(form.status)}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`${editPath}?formId=${form.id}`)}
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => navigate(`${editPath}?formId=${form.id}`)}
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Clinical Forms</h1>
            <p className="text-gray-500 mt-1">Manage patient admission, OASIS, and discharge documentation</p>
          </div>
          <Button 
            onClick={() => {
              switch (activeTab) {
                case 'oasis':
                  navigate('/dashboard/forms/oasis');
                  break;
                case 'admission':
                  navigate('/dashboard/forms/admission-consent');
                  break;
                case 'discharge':
                  navigate('/dashboard/forms/discharge');
                  break;
                default:
                  break;
              }
            }}
            className="bg-[#f39c12] hover:bg-[#e67e22] text-white"
          >
            <Plus className="w-4 h-4 mr-2" /> New Form
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {tabs.map(tab => {
            const Icon = tab.icon;
            return (
              <div 
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`bg-white rounded-lg shadow-sm border p-4 cursor-pointer transition-all ${
                  activeTab === tab.id ? 'border-[#f39c12] ring-2 ring-[#f39c12]/20' : 'hover:border-gray-300'
                }`}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${activeTab === tab.id ? 'bg-[#f39c12]/10' : 'bg-gray-100'}`}>
                      <Icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-[#f39c12]' : 'text-gray-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">{tab.label}</h3>
                      <p className="text-xs text-gray-500">{tab.description}</p>
                    </div>
                  </div>
                  <span className="text-2xl font-bold text-gray-900">{tab.count}</span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by patient name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
              />
            </div>
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-[#f39c12] focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="Draft">Draft</option>
                <option value="In Progress">In Progress</option>
                <option value="Completed">Completed</option>
                <option value="Signed">Signed</option>
              </select>
            </div>
          </div>
        </div>

        {/* Forms List */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="border-b">
            <div className="flex">
              {tabs.map(tab => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-[#f39c12] text-[#f39c12]'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                    <span className={`px-2 py-0.5 rounded-full text-xs ${
                      activeTab === tab.id ? 'bg-[#f39c12]/10' : 'bg-gray-100'
                    }`}>
                      {tab.count}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
          
          <div className="p-4">
            {renderFormList()}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default ClinicalFormsPage;
